import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TITLES = { login: "Sign In", register: "Get Free Access", forgot: "Reset Password" };

export const AuthCard = ({ mode, setMode, nextTab, accent = "cyan", note }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const green = accent === "green";
  const dest = nextTab ? `/?tab=${nextTab}` : "/";

  useEffect(() => { setErr(""); }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    if (nextTab) sessionStorage.setItem("ht_next_tab", nextTab);
    try {
      if (mode === "login") { await login(email, password); navigate(dest, { replace: true }); }
      else if (mode === "register") { await register(email, password, name); navigate(dest, { replace: true }); }
      else {
        await axios.post(`${API}/auth/forgot-password`, { email });
        toast.success("If an account exists, a reset link has been sent.");
        setMode("login");
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    if (nextTab) sessionStorage.setItem("ht_next_tab", nextTab);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = green ? "border-white/10 bg-black/60 text-white placeholder:text-slate-500 focus-visible:ring-[#22D3EE]/60" : "border-slate-800 bg-slate-900/60 text-slate-100";
  const linkTone = green ? "text-[#22D3EE] hover:text-[#A5F3FC]" : "text-cyan-400 hover:text-cyan-300";

  return (
    <div data-testid="auth-card" className={green
      ? "rounded-2xl border border-[#22D3EE]/20 bg-[#03050A]/85 p-6 shadow-[0_0_60px_rgba(34,211,238,0.08)] backdrop-blur-xl"
      : "rounded-2xl border border-cyan-900/40 bg-slate-950/70 p-6 shadow-[0_0_40px_rgba(34,211,238,0.08)] backdrop-blur-xl"}>
      <h2 className={green ? "mb-1 font-condensed text-3xl font-extrabold uppercase tracking-wide text-white" : "mb-1 font-display text-2xl font-extrabold uppercase tracking-wide text-white"}>
        {TITLES[mode]}
      </h2>
      <p className="mb-5 font-mono text-[11px] uppercase tracking-wider text-slate-500">
        {note || (mode === "forgot" ? "Enter your email to receive a reset link" : mode === "register" ? "Free account · no card required" : "Access the Hat Trick intelligence dashboard")}
      </p>

      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && (
          <Input data-testid="auth-name" aria-label="Name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className={field} />
        )}
        <Input data-testid="auth-email" aria-label="Email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
        {mode !== "forgot" && (
          <Input data-testid="auth-password" aria-label="Password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
        )}
        {err && <p data-testid="auth-error" role="alert" className="font-mono text-[11px] text-rose-400">{err}</p>}
        <Button data-testid="auth-submit" type="submit" disabled={busy}
          className={green
            ? "w-full gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 font-mono text-xs font-bold uppercase tracking-widest text-slate-950 shadow-[0_0_24px_rgba(34,211,238,0.35)] hover:brightness-110"
            : "w-full gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 font-mono text-xs font-bold uppercase tracking-widest text-slate-950 hover:from-cyan-400 hover:to-blue-400"}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "login" ? "Sign In" : mode === "register" ? "Create Free Account" : "Send Reset Link"}
        </Button>
        {mode === "register" && (
          <p data-testid="auth-legal-note" className="text-center text-[11px] leading-relaxed text-slate-500">
            By creating an account you confirm you are of legal sports-wagering age where you live and agree to the{" "}
            <a href="/terms" className="text-slate-300 underline hover:text-white">Terms</a> and{" "}
            <a href="/privacy" className="text-slate-300 underline hover:text-white">Privacy Policy</a>.
          </p>
        )}
      </form>

      {mode !== "forgot" && (
        <>
          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-600">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <button data-testid="google-login-btn" onClick={google}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-700 bg-white/95 py-2 font-sans text-sm font-semibold text-slate-800 transition-colors hover:bg-white">
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>
        </>
      )}

      <div className="mt-5 flex items-center justify-between font-mono text-[11px]">
        {mode === "login" ? (
          <>
            <button data-testid="switch-forgot" onClick={() => setMode("forgot")} className="text-slate-400 hover:text-slate-200">Forgot Password?</button>
            <button data-testid="switch-register" onClick={() => setMode("register")} className={`font-bold ${linkTone}`}>Create Account</button>
          </>
        ) : mode === "register" ? (
          <span className="text-slate-500">Already have an account? <button data-testid="switch-login" onClick={() => setMode("login")} className={`font-bold ${linkTone}`}>Sign In</button></span>
        ) : (
          <button data-testid="switch-login" onClick={() => setMode("login")} className={linkTone}>← Back to Sign In</button>
        )}
      </div>
    </div>
  );
};
